import { useState, useEffect } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, TextField, InputAdornment, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import * as XLSX from 'xlsx-js-style';

const PAYMENT_COLOR = {
  Cash: '#388e3c',
  Card: '#1565c0',
  UPI: '#6a1b9a',
  Cheque: '#f57c00',
  Online: '#00838f',
};

function BillStatementReport() {
  useSessionValidation();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { isAuthenticated, companyId, logout, token, companyName } = useAuth();

  const today = new Date().toISOString().split('T')[0];
  const nowD = new Date();
  const firstOfMonth = `${nowD.getFullYear()}-${String(nowD.getMonth() + 1).padStart(2, '0')}-01`;

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) logout();
  }, [isAuthenticated, companyId]); // eslint-disable-line

  const fetchTableData = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const response = await fetchData(
        'ReportApi',
        `/BillStatement/${companyId}/0/${fromDate}/${toDate}`,
        token
      );
      if (response && response.responseCode === '1111' && response.response_data) {
        const rows = Array.isArray(response.response_data)
          ? response.response_data
          : [response.response_data];
        setData(rows);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Bill Statement:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => { setCurrentPage(1); fetchTableData(); };

  const filteredData = data.filter(item =>
    item.ClientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.BillNumber || '').includes(searchTerm) ||
    item.Remark?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.PaymentType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Totals for footer
  const totals = filteredData.reduce((acc, item) => ({
    TotalAmount:   acc.TotalAmount   + parseFloat(item.TotalAmount   || 0),
    AdvanceAmount: acc.AdvanceAmount + parseFloat(item.AdvanceAmount || 0),
    PaidAmount:    acc.PaidAmount    + parseFloat(item.PaidAmount    || 0),
    BalanceAmount: acc.BalanceAmount + parseFloat(item.BalanceAmount || 0),
  }), { TotalAmount: 0, AdvanceAmount: 0, PaidAmount: 0, BalanceAmount: 0 });

  const headerCell = (align = 'left') => ({
    color: 'white', fontWeight: '600', fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '4px 10px', textAlign: align,
    whiteSpace: 'nowrap', backgroundColor: '#5c3d1e',
  });

  const bodyCell = (align = 'left', bold = false) => ({
    fontSize: '12px', padding: '6px 10px',
    color: '#2d3748', whiteSpace: 'nowrap',
    textAlign: align, fontWeight: bold ? '600' : '400',
  });

  const COLS = 10;

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const nowTs = new Date();
    const generatedTime = nowTs.toLocaleString('en-IN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });

    const aoa = [
      [companyName || 'Bill Statement', ...Array(COLS - 1).fill('')],
      ['Bill Statement Report', ...Array(COLS - 1).fill('')],
      [`Date Range : ${fromDate}  To : ${toDate}`, ...Array(COLS - 1).fill('')],
      [`Generated : ${generatedTime}`, ...Array(COLS - 1).fill('')],
      Array(COLS).fill(''),
      ['Bill Number', 'Bill Date', 'Client Name', 'Total Amount',
       'Advance Amount', 'Paid Amount', 'Balance Amount', 'Payment Type', 'Remark'],
      ...filteredData.map(item => [
        item.BillNumber || '',
        item.BillDate ? item.BillDate.split('T')[0] : '',
        item.ClientName || '',
        parseFloat(item.TotalAmount   || 0),
        parseFloat(item.AdvanceAmount || 0),
        parseFloat(item.PaidAmount    || 0),
        parseFloat(item.BalanceAmount || 0),
        item.PaymentType || '',
        item.Remark || '',
      ]),
      // Totals row
      ['', '', 'TOTAL',
        totals.TotalAmount, totals.AdvanceAmount,
        totals.PaidAmount, totals.BalanceAmount, '', ''],
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!cols'] = [
      { wch: 13 }, { wch: 12 }, { wch: 22 }, { wch: 14 },
      { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 28 },
    ];

    ws['!rows'] = [
      { hpt: 22 }, { hpt: 18 }, { hpt: 16 }, { hpt: 16 }, { hpt: 6 }, { hpt: 20 },
    ];

    ws['!merges'] = [0, 1, 2, 3, 4].map(r => ({ s: { r, c: 0 }, e: { r, c: COLS - 1 } }));

    const infoS = (sz, bold, color = '5c3d1e') => ({
      font: { bold, sz, color: { rgb: color } },
      alignment: { horizontal: 'center', vertical: 'center' },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } },
    });

    ws['A1'].s = infoS(16, true);
    ws['A2'].s = infoS(13, true, '2d3748');
    ws['A3'].s = infoS(11, false, '444444');
    ws['A4'].s = infoS(11, false, '444444');

    const hdrS = {
      font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '5c3d1e' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top:    { style: 'medium', color: { rgb: '3b2410' } },
        bottom: { style: 'medium', color: { rgb: '3b2410' } },
        left:   { style: 'thin',   color: { rgb: '3b2410' } },
        right:  { style: 'thin',   color: { rgb: '3b2410' } },
      },
    };

    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: 5, c });
      if (ws[addr]) ws[addr].s = hdrS;
    }

    const numericCols = new Set([3, 4, 5, 6]);
    const dataRowCount = filteredData.length;

    for (let r = 6; r < 6 + dataRowCount; r++) {
      const even = (r - 6) % 2 === 0;
      for (let c = 0; c < COLS; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = {
          font: { sz: 10 },
          fill: { patternType: 'solid', fgColor: { rgb: even ? 'FFFFFF' : 'FDF6EC' } },
          alignment: { horizontal: numericCols.has(c) ? 'right' : 'left', vertical: 'center' },
          border: {
            top:    { style: 'thin', color: { rgb: 'E0E0E0' } },
            bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
            left:   { style: 'thin', color: { rgb: 'E0E0E0' } },
            right:  { style: 'thin', color: { rgb: 'E0E0E0' } },
          },
        };
      }
    }

    // Style totals row
    const totalRow = 6 + dataRowCount;
    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: totalRow, c });
      if (!ws[addr]) ws[addr] = { t: 's', v: '' };
      ws[addr].s = {
        font: { bold: true, sz: 11, color: { rgb: '5c3d1e' } },
        fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } },
        alignment: { horizontal: numericCols.has(c) ? 'right' : c === 2 ? 'right' : 'left', vertical: 'center' },
        border: {
          top:    { style: 'medium', color: { rgb: '5c3d1e' } },
          bottom: { style: 'medium', color: { rgb: '5c3d1e' } },
          left:   { style: 'thin',   color: { rgb: 'E0E0E0' } },
          right:  { style: 'thin',   color: { rgb: 'E0E0E0' } },
        },
      };
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Bill Statement');
    XLSX.writeFile(wb, `BillStatement_${fromDate}_${toDate}.xlsx`, { bookSST: false, cellStyles: true });
  };

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Bill Statement..." />}

        <div style={{
          margin: '0 auto', background: 'white', borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px',
          color: 'black', fontFamily: 'Times New Roman', overflow: 'hidden',
        }}>
          {/* Title */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>🧾 Bill Statement</span>
            </h4>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>To date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />
            <Button variant="contained" onClick={handleRefresh}
              style={{ background: 'linear-gradient(135deg, #388e3c 0%, #43a047 100%)', color: 'white', padding: '4px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(56,142,60,0.4)' }}>
              Refresh
            </Button>
            <Button variant="contained" onClick={handleExportExcel} disabled={filteredData.length === 0}
              style={{ background: filteredData.length === 0 ? '#ccc' : 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', color: 'white', padding: '4px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: filteredData.length === 0 ? 'none' : '0 2px 8px rgba(21,101,192,0.4)' }}>
              📥 Excel
            </Button>
            <div style={{ flex: 1 }} />
            <TextField size="small" placeholder="Search..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '18px' }} /></InputAdornment> }}
              sx={{ minWidth: '180px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }} />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <style>{`
              .bs-scroll::-webkit-scrollbar { height: 10px; width: 8px; }
              .bs-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
              .bs-scroll::-webkit-scrollbar-thumb { background: #c9a84c; border-radius: 4px; }
              .bs-scroll::-webkit-scrollbar-thumb:hover { background: #5c3d1e; }
              .bs-scroll { scrollbar-width: thin; scrollbar-color: #c9a84c #f1f1f1; }
            `}</style>
            <div className="bs-scroll" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, overflow: 'visible' }}>
                <Table stickyHeader sx={{ minWidth: 900 }} aria-label="bill statement table">
                  <TableHead>
                    <TableRow style={{ height: '27px' }}>
                      <TableCell style={headerCell()}>Bill No</TableCell>
                      <TableCell style={headerCell()}>Bill Date</TableCell>
                      <TableCell style={headerCell()}>Client Name</TableCell>
                      <TableCell style={headerCell('right')}>Total Amount</TableCell>
                      <TableCell style={headerCell('right')}>Advance</TableCell>
                      <TableCell style={headerCell('right')}>Paid</TableCell>
                      <TableCell style={headerCell('right')}>Balance</TableCell>
                      <TableCell style={headerCell('center')}>Payment Type</TableCell>
                      <TableCell style={headerCell()}>Remark</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                          {loading ? 'Loading...' : 'No records found. Select date range and click Refresh.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {paginatedData.map((item, index) => (
                          <TableRow key={item.BillId || index}
                            style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '1px solid #f0f0f0' }}>
                            <TableCell style={bodyCell('left', true)}>{item.BillNumber}</TableCell>
                            <TableCell style={bodyCell()}>{item.BillDate ? item.BillDate.split('T')[0] : '—'}</TableCell>
                            <TableCell style={bodyCell('left', true)}>{item.ClientName || '—'}</TableCell>
                            <TableCell style={bodyCell('right', true)}>{parseFloat(item.TotalAmount || 0).toLocaleString('en-IN')}</TableCell>
                            <TableCell style={bodyCell('right')}>{parseFloat(item.AdvanceAmount || 0).toLocaleString('en-IN')}</TableCell>
                            <TableCell style={bodyCell('right')}>{parseFloat(item.PaidAmount || 0).toLocaleString('en-IN')}</TableCell>
                            <TableCell style={bodyCell('right', true)}>{parseFloat(item.BalanceAmount || 0).toLocaleString('en-IN')}</TableCell>
                            <TableCell style={bodyCell('center')}>
                              {item.PaymentType ? (
                                <Chip label={item.PaymentType} size="small"
                                  style={{ fontSize: '10px', height: '18px', backgroundColor: PAYMENT_COLOR[item.PaymentType] || '#757575', color: 'white', fontWeight: '600' }} />
                              ) : '—'}
                            </TableCell>
                            <TableCell style={bodyCell()}>{item.Remark || '—'}</TableCell>
                          </TableRow>
                        ))}
                        {/* Totals footer row */}
                        <TableRow style={{ background: '#fff8ee', borderTop: '2px solid #c9a84c' }}>
                          <TableCell colSpan={3} style={{ fontSize: '12px', padding: '6px 10px', fontWeight: '700', color: '#5c3d1e', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            TOTAL ({filteredData.length} records)
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '6px 10px', fontWeight: '700', color: '#5c3d1e', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {totals.TotalAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '6px 10px', fontWeight: '700', color: '#5c3d1e', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {totals.AdvanceAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '6px 10px', fontWeight: '700', color: '#5c3d1e', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {totals.PaidAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '6px 10px', fontWeight: '700', color: '#5c3d1e', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {totals.BalanceAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages}
            totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={n => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>
    </Fade>
  );
}

export default BillStatementReport;
